// ============================================
// components/ReportModal.tsx
// 通用举报弹窗 (评论 / 作品共用)
//
// 用法:
//   const [reportTarget, setReportTarget] = useState<{type:'comment'|'project'; shortId:string}|null>(null);
//   {reportTarget && (
//     <ReportModal
//       targetType={reportTarget.type}
//       targetShortId={reportTarget.shortId}
//       onClose={() => setReportTarget(null)}
//     />
//   )}
//
// 后端: community-report-submit  { target_type, target_short_id, reason, detail? }
// ============================================

import { useState } from 'react';
import { authedCallApi } from '../services/auth';
import { requireLogin } from '../services/loginPrompt';

const REASONS: { code: string; label: string }[] = [
  { code: 'spam', label: '垃圾广告' },
  { code: 'porn', label: '色情低俗' },
  { code: 'abuse', label: '人身攻击/辱骂' },
  { code: 'illegal', label: '违法违规' },
  { code: 'other', label: '其他' },
];

interface Props {
  targetType: 'comment' | 'project';
  targetShortId: string;
  onClose: () => void;
  onDone?: (msg: string) => void;   // 提交成功回调 (可选, 用于外层 toast)
}

export default function ReportModal({ targetType, targetShortId, onClose, onDone }: Props) {
  const [reason, setReason] = useState<string>('');
  const [detail, setDetail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [hint, setHint] = useState('');

  const targetLabel = targetType === 'comment' ? '评论' : '作品';

  async function submit() {
    if (!reason) { setHint('请选择举报理由'); return; }
    if (!requireLogin()) return;
    setSubmitting(true);
    setHint('');
    const r = await authedCallApi<{ report_id: string; message?: string }>('community-report-submit', {
      target_type: targetType,
      target_short_id: targetShortId,
      reason,
      detail: detail.trim() || undefined,
    });
    setSubmitting(false);
    if (r.ok) {
      onDone?.(r.message || '举报已提交, 感谢你帮助维护创新工场');
      onClose();
    } else {
      // 已举报过 / 不能举报自己 等, 后端给的 message 直接显示
      setHint(r.message || r.error || '举报失败, 请稍后重试');
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(420px, 100%)', background: 'white', borderRadius: 16,
          padding: 20, boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
        }}
      >
        <div style={{ fontSize: 17, fontWeight: 700, color: '#1f2937', marginBottom: 4 }}>
          举报{targetLabel}
        </div>
        <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 16 }}>
          请选择举报理由,我们会尽快人工核实处理。
        </div>

        {/* 理由单选 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
          {REASONS.map((r) => (
            <button
              key={r.code}
              onClick={() => setReason(r.code)}
              style={{
                textAlign: 'left', padding: '10px 14px', borderRadius: 10,
                border: reason === r.code ? '1.5px solid #f0a020' : '1px solid #e5e7eb',
                background: reason === r.code ? '#fffbeb' : 'white',
                color: '#374151', fontSize: 14, fontWeight: reason === r.code ? 600 : 400,
                cursor: 'pointer', transition: 'all 0.12s',
              }}
            >
              {r.label}
            </button>
          ))}
        </div>

        {/* 补充说明 (选填) */}
        <textarea
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
          placeholder="补充说明 (选填)"
          maxLength={300}
          rows={3}
          style={{
            width: '100%', boxSizing: 'border-box', padding: '10px 12px',
            border: '1px solid #e5e7eb', borderRadius: 10, fontSize: 13,
            resize: 'none', outline: 'none', marginBottom: 6, fontFamily: 'inherit',
          }}
        />

        {hint && <div style={{ fontSize: 13, color: '#dc2626', marginBottom: 10 }}>{hint}</div>}

        {/* 按钮 */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
          <button
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: '9px 18px', border: '1px solid #e5e7eb', borderRadius: 10,
              background: 'white', color: '#374151', fontSize: 14, cursor: 'pointer',
            }}
          >
            取消
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            style={{
              padding: '9px 20px', border: 'none', borderRadius: 10,
              background: submitting ? '#fbbf77' : '#f0a020', color: 'white',
              fontSize: 14, fontWeight: 600, cursor: submitting ? 'default' : 'pointer',
            }}
          >
            {submitting ? '提交中…' : '提交举报'}
          </button>
        </div>
      </div>
    </div>
  );
}
